"use client";

import { IconCode, IconMenu2, IconRocket, IconSearch } from "@tabler/icons-react";
import { type MouseEvent, useCallback, useEffect, useState } from "react";
import Breadcrumbs from "src/components/common/Breadcrumbs/Breadcrumbs";
import Button from "src/components/common/Button/Button";
import IconButton from "src/components/common/IconButton/IconButton";
import Kbd from "src/components/common/Kbd/Kbd";
import { useAuth } from "src/lib/contexts/AuthContext";
import { usePortal } from "src/lib/contexts/PortalContext";
import { lazyComponent, loadLazyModule } from "src/lib/LazyModules";
import { usePathname } from "src/lib/Navigation";
import { isReviewerPortal } from "src/lib/ReviewerPortal";
import { useDocsBreadcrumbs } from "src/lib/TopbarBreadcrumbs";
import { useExtracted } from "#i18n";

// Signed-in visitors only; the layouts preload it for them (src/lib/LazyModules.tsx).
const SignedInTopbarActions = lazyComponent("signedInShell", "SignedInTopbarActions");
const TopbarScopeTrail = lazyComponent("signedInShell", "TopbarScopeTrail");
// Loaded when search first opens (or is about to: hover, focus), for every visitor.
const AssetSearchDialog = lazyComponent("assetSearchDialog", "default");
const preloadAssetSearch = () => {
  void loadLazyModule("assetSearchDialog").catch(() => {});
};

interface PortalTopbarProps {
  onOpenNavigation?: () => void;
}

const PortalTopbar = (props: PortalTopbarProps) => {
  const { onOpenNavigation } = props;
  const translateMessage = useExtracted();
  const pathname = usePathname();
  const [assetSearchVisible, setAssetSearchVisible] = useState(false);
  // The dialog mounts the first time search opens and stays mounted (so it can animate closed).
  const [assetSearchMounted, setAssetSearchMounted] = useState(false);
  const { projectTeam } = usePortal();
  const docsBreadcrumbs = useDocsBreadcrumbs(pathname);
  const isDocs = pathname.startsWith("/docs");
  const signedIn = Boolean(useAuth().authState?.user);
  // Reviewers can't search or follow project assets; they only work the review queue.
  const reviewer = isReviewerPortal(projectTeam);
  const docsSection =
    pathname === "/docs"
      ? "quick-start"
      : pathname.startsWith("/docs/api")
        ? "api-reference"
        : undefined;

  // Opens right away; before its chunk has loaded, the dialog appears as soon as it arrives.
  const openAssetSearch = useCallback(() => {
    setAssetSearchMounted(true);
    setAssetSearchVisible(true);
  }, []);
  const assetSearchDialog = assetSearchMounted ? (
    <AssetSearchDialog visible={assetSearchVisible} onVisibleChange={setAssetSearchVisible} />
  ) : null;

  useEffect(() => {
    if (reviewer) return;

    const handleShortcut = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        openAssetSearch();
      }
    };
    document.addEventListener("keydown", handleShortcut);
    return () => document.removeEventListener("keydown", handleShortcut);
  }, [isDocs, reviewer, openAssetSearch]);

  return (
    <header className="flex h-16 items-center justify-between gap-2 px-3 sm:px-6 lg:px-8">
      <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
        {onOpenNavigation ? (
          <IconButton
            label={translateMessage("Open navigation")}
            labelTooltip={false}
            variant="bare"
            className="shrink-0 md:!hidden"
            onClick={(event: MouseEvent<HTMLButtonElement>) => {
              event.currentTarget.focus();
              onOpenNavigation();
            }}
          >
            <IconMenu2 size={20} />
          </IconButton>
        ) : null}
        {isDocs ? (
          docsBreadcrumbs.length > 1 ? (
            <Breadcrumbs className="min-w-0 !flex-nowrap">
              <Breadcrumbs.List nowrap>
                {docsBreadcrumbs.map((breadcrumb, index) => {
                  const last = index === docsBreadcrumbs.length - 1;
                  return (
                    <Breadcrumbs.Breadcrumb
                      key={`${breadcrumb.href ?? "current"}-${breadcrumb.label}`}
                      href={breadcrumb.href}
                      isCurrentPage={last}
                      hiddenBelow={!last ? "md" : undefined}
                      className={last ? "min-w-0 shrink" : "min-w-0 shrink-0"}
                    >
                      <span className="truncate">{breadcrumb.label}</span>
                    </Breadcrumbs.Breadcrumb>
                  );
                })}
              </Breadcrumbs.List>
            </Breadcrumbs>
          ) : null
        ) : signedIn ? (
          // A portal page's scopes, each a switcher: `Organisation / Project` or
          // `Organisation / Policy / Category`.
          <TopbarScopeTrail />
        ) : null}
      </div>

      <div className="flex shrink-0 items-center gap-1 sm:gap-3">
        {isDocs ? (
          <nav
            aria-label={translateMessage("Documentation sections")}
            className="hidden items-center gap-1 lg:flex"
          >
            <Button
              href="/docs"
              variant="bare"
              label={translateMessage("Quick start")}
              aria-current={docsSection === "quick-start" ? "page" : undefined}
            >
              <IconRocket size={16} aria-hidden />
              {translateMessage("Quick start")}
            </Button>
            <Button
              href="/docs/api"
              variant="bare"
              label={translateMessage("API reference")}
              aria-current={docsSection === "api-reference" ? "page" : undefined}
            >
              <IconCode size={16} aria-hidden />
              {translateMessage("API reference")}
            </Button>
          </nav>
        ) : reviewer ? null : (
          <>
            <IconButton
              label={translateMessage("Search")}
              labelTooltip={false}
              variant="bare"
              className="lg:!hidden"
              onClick={openAssetSearch}
              onPointerEnter={preloadAssetSearch}
              onFocus={preloadAssetSearch}
            >
              <IconSearch size={20} />
            </IconButton>
            <Button
              label={translateMessage("Search")}
              variant="outline"
              ripple={false}
              aria-keyshortcuts="Meta+K Control+K"
              className="!hidden w-80 !justify-start lg:!flex"
              onClick={openAssetSearch}
              onPointerEnter={preloadAssetSearch}
              onFocus={preloadAssetSearch}
            >
              <IconSearch size={18} aria-hidden />
              <span className="min-w-0 truncate font-normal text-muted">
                {translateMessage("Search settings, docs, assets...")}
              </span>
              <span aria-hidden className="ms-auto flex shrink-0 gap-1">
                <Kbd>⌘</Kbd>
                <Kbd>K</Kbd>
              </span>
            </Button>
            {assetSearchDialog}
          </>
        )}
        {isDocs && (
          <>
            <IconButton
              label={translateMessage("Search")}
              variant="bare"
              onClick={openAssetSearch}
              onPointerEnter={preloadAssetSearch}
              onFocus={preloadAssetSearch}
            >
              <IconSearch size={20} />
            </IconButton>
            {assetSearchDialog}
          </>
        )}
        {signedIn ? <SignedInTopbarActions /> : null}
      </div>
    </header>
  );
};

export default PortalTopbar;
